/**
 * Sections of the settings hub.
 *
 * Before consolidation there were two disconnected surfaces: `/settings` (one
 * 541-line scroll mixing personal prefs with superuser access control) and
 * `/crm/settings/*` (eleven config pages with no sub-nav and no link back to
 * anything). Neither told you what else existed. This is the index.
 */
import { useMemo } from 'react';
import {
  Bell,
  Bookmark,
  Bot,
  History,
  Layers,
  Mail,
  Megaphone,
  MessageCircle,
  Palette,
  Share2,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Timer,
  UserCircle,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useAccess } from '@/features/AccessProvider';

export interface SettingsNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Resource slug required to see this entry. */
  slug?: string;
  superuserOnly?: boolean;
  /** Match exactly — used for section landing pages with children. */
  exact?: boolean;
}

export interface SettingsNavSection {
  id: string;
  label: string;
  items: SettingsNavItem[];
}

export const SETTINGS_SECTIONS: SettingsNavSection[] = [
  {
    id: 'account',
    label: 'Account',
    items: [
      { path: '/settings/account', label: 'Profile', icon: UserCircle },
      { path: '/settings/appearance', label: 'Appearance', icon: Palette },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    items: [
      { path: '/settings/preferences', label: 'Preferences', icon: SlidersHorizontal },
      { path: '/settings/views', label: 'Saved views', icon: Bookmark },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { path: '/settings/users', label: 'Users', icon: Users, slug: 'settings.user-management', exact: true },
      { path: '/settings/access', label: 'Access control', icon: ShieldCheck, superuserOnly: true },
      { path: '/settings/audit', label: 'Audit log', icon: History, slug: 'audit' },
    ],
  },
  {
    id: 'support',
    label: 'Support CRM',
    items: [
      { path: '/settings/support', label: 'Overview', icon: Layers, slug: 'crm.settings', exact: true },
      { path: '/settings/support/routing', label: 'Routing matrix', icon: ShieldAlert, slug: 'crm.settings.routing' },
      { path: '/settings/support/sla', label: 'SLA & breach policy', icon: Timer, slug: 'crm.settings.sla' },
      { path: '/settings/support/reason-codes', label: 'Reason codes', icon: Layers, slug: 'crm.settings.reason_codes' },
      { path: '/settings/support/cancellations', label: 'Cancellation sources', icon: ShieldAlert, slug: 'crm.settings.cancellations' },
      { path: '/settings/support/email', label: 'Email (SMTP)', icon: Mail, slug: 'crm.settings.email' },
      { path: '/settings/support/whatsapp', label: 'WhatsApp', icon: MessageCircle, slug: 'crm.settings.whatsapp' },
      { path: '/settings/support/meta', label: 'Meta channels', icon: Share2, slug: 'crm.settings.meta' },
      { path: '/settings/support/ai', label: 'AI agent', icon: Bot, slug: 'crm.settings.ai' },
      { path: '/settings/support/templates', label: 'Notification templates', icon: Bell, slug: 'crm.settings.templates' },
      { path: '/settings/support/canned-replies', label: 'Canned replies', icon: Zap, slug: 'crm.settings.canned_replies' },
      { path: '/settings/support/engagement', label: 'Engagement & operations', icon: Megaphone, slug: 'crm.settings.engagement' },
    ],
  },
];

/**
 * Sections filtered to what this user may open, so a non-superuser never sees
 * an Administration heading they can't act on.
 */
export function useSettingsSections(): SettingsNavSection[] {
  const { can, isSuperuser } = useAccess();
  return useMemo(
    () =>
      SETTINGS_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.superuserOnly) return isSuperuser;
          if (!item.slug) return true;
          return isSuperuser || can(item.slug);
        }),
      })).filter((section) => section.items.length > 0),
    [can, isSuperuser],
  );
}
